import React, { useEffect, useRef, useState } from 'react';
import { Download, ExternalLink, FileText, Loader2, MapPin, Contact, X, AlertTriangle, Play } from 'lucide-react';
import { chatsService } from '../../../services/api';
import Boton from './Boton';

const TIPOS_MEDIA = new Set(['image', 'sticker', 'ptt', 'audio', 'video', 'document']);
export const esMedia = (m) => TIPOS_MEDIA.has(m.tipo);
export const esNoTexto = (m) => esMedia(m) || ['location', 'vcard', 'multi_vcard'].includes(m.tipo);

const fmtTamano = (b) => {
  if (!b) return null;
  if (b < 1024 * 1024) return `${Math.max(1, Math.round(b / 1024))} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};
const fmtDuracion = (s) => (s ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}` : null);

// Descarga el archivo con la sesion y entrega un blob URL; se libera al desmontar.
// "activo" difiere la descarga (visibilidad en pantalla o clic).
const useBlob = (chatId, m, activo) => {
  const [estado, setEstado] = useState({ url: null, cargando: false, error: false });
  useEffect(() => {
    if (!activo) return undefined;
    let cancelado = false;
    let url = null;
    setEstado({ url: null, cargando: true, error: false });
    chatsService.media(chatId, m.id)
      .then((blob) => {
        if (cancelado) return;
        url = URL.createObjectURL(blob);
        setEstado({ url, cargando: false, error: false });
      })
      .catch(() => { if (!cancelado) setEstado({ url: null, cargando: false, error: true }); });
    return () => {
      cancelado = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [chatId, m.id, activo]);
  return estado;
};

// true cuando el elemento entra en pantalla (una sola vez).
const useVisible = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || visible) return undefined;
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return undefined; }
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { rootMargin: '200px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [visible]);
  return [ref, visible];
};

const Cargando = () => (
  <span className="inline-flex items-center gap-1.5 text-xs text-gray-500" role="status"><Loader2 size={14} className="animate-spin" aria-hidden="true" /> Cargando archivo...</span>
);
const ErrorCarga = () => (
  <span className="inline-flex items-center gap-1.5 text-xs text-red-700" role="alert"><AlertTriangle size={14} aria-hidden="true" /> No se pudo cargar el archivo</span>
);

const Visor = ({ url, onClose }) => {
  useEffect(() => {
    const alPresionar = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', alPresionar);
    return () => document.removeEventListener('keydown', alPresionar);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" role="dialog" aria-modal="true" aria-label="Imagen ampliada" onClick={onClose}>
      <Boton variante="icono" etiqueta="Cerrar" icono={X} onClick={onClose} className="absolute right-3 top-3 !text-white hover:!bg-white/20" autoFocus />
      <img src={url} alt="Imagen ampliada" className="max-h-full max-w-full object-contain" onClick={(e) => e.stopPropagation()} />
    </div>
  );
};

const Imagen = ({ chatId, m, sticker }) => {
  const [ref, visible] = useVisible();
  const { url, cargando, error } = useBlob(chatId, m, visible);
  const [ampliada, setAmpliada] = useState(false);
  return (
    <div ref={ref} className={sticker ? 'min-h-[40px]' : 'min-h-[80px] min-w-[120px]'}>
      {cargando && <Cargando />}
      {error && <ErrorCarga />}
      {url && (sticker
        ? <img src={url} alt="Sticker" className="h-28 w-28 object-contain" />
        : (
          <button type="button" onClick={() => setAmpliada(true)} className="block rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" aria-label="Ver imagen ampliada" title="Ver ampliada">
            <img src={url} alt={m.leyenda || 'Imagen del chat'} className="max-h-60 max-w-full rounded object-cover" />
          </button>
        ))}
      {ampliada && url && <Visor url={url} onClose={() => setAmpliada(false)} />}
    </div>
  );
};

// Audio y video pesan mas: se descargan al pulsar, no al verse.
const Reproductor = ({ chatId, m, video }) => {
  const [pedido, setPedido] = useState(false);
  const { url, cargando, error } = useBlob(chatId, m, pedido);
  const dur = fmtDuracion(m.duracion);
  const etiqueta = video ? 'Video' : m.tipo === 'ptt' ? 'Nota de voz' : 'Audio';
  if (url) {
    return video
      ? <video src={url} controls preload="metadata" className="max-h-60 max-w-full rounded" />
      : <audio src={url} controls preload="metadata" className="w-60 max-w-full" />;
  }
  if (cargando) return <Cargando />;
  return (
    <div className="flex flex-col items-start gap-1">
      <Boton icono={Play} onClick={() => setPedido(true)} etiqueta={`Cargar ${etiqueta.toLowerCase()}`}>{etiqueta}{dur ? ` (${dur})` : ''}</Boton>
      {error && <ErrorCarga />}
    </div>
  );
};

const Documento = ({ chatId, m }) => {
  const [pedido, setPedido] = useState(false);
  const { url, cargando, error } = useBlob(chatId, m, pedido);
  const nombre = m.nombreArchivo || 'Documento';
  const esPdf = m.mimetype === 'application/pdf';
  return (
    <div className="flex items-center gap-3 rounded border border-gray-200 bg-gray-50 p-2">
      <FileText size={28} className="shrink-0 text-gray-500" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-900" title={nombre}>{nombre}</p>
        <p className="text-xs text-gray-500">{[esPdf ? 'PDF' : null, fmtTamano(m.tamano)].filter(Boolean).join(' - ') || 'Archivo'}</p>
        {error && <ErrorCarga />}
      </div>
      {url ? (
        <div className="flex shrink-0 gap-1">
          {esPdf && <a href={url} target="_blank" rel="noopener noreferrer" title="Abrir en pestaña nueva" aria-label="Abrir en pestaña nueva" className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 text-gray-700 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"><ExternalLink size={16} aria-hidden="true" /></a>}
          <a href={url} download={nombre} title="Descargar" aria-label="Descargar" className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 text-gray-700 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"><Download size={16} aria-hidden="true" /></a>
        </div>
      ) : (
        <Boton icono={Download} cargando={cargando} onClick={() => setPedido(true)} etiqueta="Preparar archivo para abrir o descargar">{error ? 'Reintentar' : 'Obtener'}</Boton>
      )}
    </div>
  );
};

const MensajeMedia = ({ chatId, m }) => {
  let cuerpo;
  if (m.tipo === 'image') cuerpo = <Imagen chatId={chatId} m={m} />;
  else if (m.tipo === 'sticker') cuerpo = <Imagen chatId={chatId} m={m} sticker />;
  else if (m.tipo === 'ptt' || m.tipo === 'audio') cuerpo = <Reproductor chatId={chatId} m={m} />;
  else if (m.tipo === 'video') cuerpo = <Reproductor chatId={chatId} m={m} video />;
  else if (m.tipo === 'document') cuerpo = <Documento chatId={chatId} m={m} />;
  else if (m.tipo === 'location') {
    const u = m.ubicacion;
    cuerpo = (
      <span className="inline-flex items-start gap-1.5">
        <MapPin size={16} className="mt-0.5 shrink-0 text-red-600" aria-hidden="true" />
        <span>
          Ubicación{u?.nombre ? `: ${u.nombre}` : ''}
          {u && Number.isFinite(u.lat) && Number.isFinite(u.lng) && (
            <> <a className="text-blue-700 underline" target="_blank" rel="noopener noreferrer" href={`https://www.google.com/maps?q=${u.lat},${u.lng}`}>Ver en el mapa</a></>
          )}
        </span>
      </span>
    );
  } else {
    cuerpo = <span className="inline-flex items-center gap-1.5"><Contact size={16} className="text-gray-500" aria-hidden="true" /> Contacto{m.contacto?.nombre ? `: ${m.contacto.nombre}` : ''}</span>;
  }
  return (
    <div className="space-y-1">
      {cuerpo}
      {m.leyenda && <p className="whitespace-pre-wrap break-words">{m.leyenda}</p>}
    </div>
  );
};

export default MensajeMedia;
